import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { ChartBar, GameController, Trophy, GraduationCap, BookOpen, Megaphone } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublisherInsights() {
  const { publisherId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/publisher-portal/${publisherId}/insights`, { withCredentials: true })
      .then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [publisherId]);

  if (loading) return <div className="h-64 bg-white/5 rounded-xl animate-pulse" />;
  if (!data) return <p className="text-[#666]">Failed to load insights.</p>;

  return (
    <div data-testid="publisher-insights-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Unbounded' }}>Insights</h1>
        <p className="text-sm text-[#666] mt-1">Content performance and community engagement</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
          <GameController size={20} weight="duotone" className="text-[#9D4CDD] mb-2" />
          <p className="text-2xl font-bold text-white">{data.games_count}</p>
          <p className="text-xs text-[#666]">Published Games</p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
          <ChartBar size={20} weight="duotone" className="text-[#00F0FF] mb-2" />
          <p className="text-2xl font-bold text-white">{data.total_sessions}</p>
          <p className="text-xs text-[#666]">Total Play Sessions</p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
          <Trophy size={20} weight="duotone" className="text-[#F59E0B] mb-2" />
          <p className="text-2xl font-bold text-white">{data.total_tournaments}</p>
          <p className="text-xs text-[#666]">Tournaments Held</p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
          <BookOpen size={20} weight="duotone" className="text-[#00F0FF] mb-2" />
          <p className="text-2xl font-bold text-white">{data.published_rulesets}<span className="text-sm text-[#555]">/{data.total_rulesets}</span></p>
          <p className="text-xs text-[#666]">Published Rulesets</p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
          <GraduationCap size={20} weight="duotone" className="text-[#10B981] mb-2" />
          <p className="text-2xl font-bold text-white">{data.published_tutorials}<span className="text-sm text-[#555]">/{data.total_tutorials}</span></p>
          <p className="text-xs text-[#666]">Published Tutorials</p>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
          <Megaphone size={20} weight="duotone" className="text-[#F59E0B] mb-2" />
          <p className="text-2xl font-bold text-white">{data.active_campaigns}<span className="text-sm text-[#555]">/{data.total_campaigns}</span></p>
          <p className="text-xs text-[#666]">Active Campaigns</p>
        </div>
      </div>

      {/* Game Breakdown */}
      {data.game_breakdown?.length > 0 && (
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-sm font-semibold text-white mb-4">Sessions by Game</h3>
          <div className="space-y-3">
            {data.game_breakdown.map((g, i) => {
              const max = Math.max(...data.game_breakdown.map(x => x.sessions), 1);
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs text-[#888] w-32 text-right truncate">{g.game}</span>
                  <div className="flex-1 h-6 bg-white/[0.03] rounded-md overflow-hidden">
                    <div className="h-full rounded-md bg-gradient-to-r from-[#F59E0B]/60 to-[#F59E0B] transition-all"
                      style={{ width: `${Math.max((g.sessions / max) * 100, 4)}%` }} />
                  </div>
                  <span className="text-xs text-white font-medium w-12 text-right">{g.sessions}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
